import {
  FormStore,
  GENERIC_VALIDATION_NOTIFICATION,
  GlobalStore,
} from '@roc/feature-app-core';
import { action, flow, makeObservable, observable } from 'mobx';
import {
  downloadDocument,
  GENERIC_ERROR_MESSAGE,
  isEmptyString,
} from '@roc/feature-utils';
import { AppraisalDetailsV2Service } from '../../../services/documentForms/appraisalDetailsV2/appraisalDetailsV2Service';
import { LoanStore } from 'libs/feature-loans/src/loans/stores/loanStore';
import { FileUpload } from '@roc/ui';

const form = {
  fields: {
    propertyIds: {
      value: [],
      error: null,
      rule: '',
    },
    clientValuationReferenceIds: {
      value: [],
      error: null,
      rule: '',
    },
    reason: {
      value: '',
      error: null,
      rule: '',
    },
    amount: {
      value: 0,
      error: null,
      rule: '',
    },
  },
  meta: {
    isValid: true,
    error: null,
  },
};

export class AppraisalDetailsV2ActionsStore {
  private globalStore: GlobalStore;
  private loanStore: LoanStore;
  private appraisalService: AppraisalDetailsV2Service;
  public appraisalActionsFormStore: FormStore;
  private appraisalDetailsV2Service: AppraisalDetailsV2Service;

  loanId?: number;
  uploadedFile: FileUpload | null = null;
  ordersByProperyId: Record<number, {
    clientValuationReferenceId: number;
    vendorOrderNumber: string
  }[]> = {};
  linkOrderLookupResult: { orderExists: boolean; resultFound: boolean; isOrderInNegativeStatus?: boolean; paymentsCompleted?: boolean; loanId?: number | null; dealName?: string | null; address?: string | null; loanPropertyValuationId?: number | null; propertyValuationId?: number | null } | null = null;
  isLinkOrderLoading: boolean = false;

  constructor(globalStore: GlobalStore, loanStore: LoanStore) {
    this.globalStore = globalStore;
    this.loanStore = loanStore;
    this.appraisalDetailsV2Service = new AppraisalDetailsV2Service();
    this.appraisalActionsFormStore = new FormStore(form, globalStore);
    this.appraisalService = new AppraisalDetailsV2Service();

    makeObservable(this, {
      reset: action,
      uploadedFile: observable,
      setUploadedFile: action,
      createExtraPaymentLink: flow,
      validateForm: action,
      loanId: observable,
      ordersByProperyId: observable,
      fetchPropertyValuations: flow,
      downloadValuationReport: flow,
      submitReconsiderationOfValue: flow,
      startDispute: flow,
      submitRevision: flow,
      valuationReorder: flow,
      valuationRecertification: flow,
      valuationTransfer: flow,
      linkOrderLookupResult: observable,
      isLinkOrderLoading: observable,
      lookupOrder: flow,
      submitLinkOrder: flow,
      resetLinkOrder: action,
      createLpvAndPaymentLink: flow,
      pushNewOrder: flow,
      setAsSelectedPrimary: flow,
    });
  }

  reset() {
    this.loanId = null;
    this.ordersByProperyId = {};
    this.uploadedFile = null;
    this.appraisalActionsFormStore.reset();
  }

  validateForm({
    propertyRequired,
    orderRequired,
    reasonRequired,
    amountRequired,
    uploadRequired,
  }: {
    propertyRequired?: boolean;
    orderRequired?: boolean;
    reasonRequired?: boolean;
    amountRequired?: boolean;
    uploadRequired?: boolean;
  }) {
    const {
      propertyIds,
      clientValuationReferenceIds,
      reason,
      amount,
    } = this.appraisalActionsFormStore.form.fields;
    let isValid = true;
    if (propertyRequired && propertyIds.value.length === 0) {
      propertyIds.error = 'Please select at least one property';
      isValid = false;
    }
    if (orderRequired && clientValuationReferenceIds.value.length === 0) {
      clientValuationReferenceIds.error = 'Please select at least one order';
      isValid = false;
    }
    if (reasonRequired && isEmptyString(reason.value)) {
      reason.error = 'Please provide a reason';
      isValid = false;
    }
    if (amountRequired && (!amount.value || amount.value <= 0)) {
      amount.error = 'Please provide an amount';
      isValid = false;
    }
    if (uploadRequired && !this.uploadedFile) {
      this.globalStore.notificationStore.showWarningNotification({
        message: 'Please upload a file',
      });
      isValid = false;
    }
    return isValid;
  }

  setUploadedFile = (file: FileUpload) => {
    this.uploadedFile = file;
  };

  *fetchPropertyValuations() {
    try {
      this.loanId = this.loanStore.loanDetails?.loanId;
      const response = yield this.appraisalDetailsV2Service.getPropertyValuationsByLoanId(
        this.loanId
      );
      const data = response.data.data ?? {};
      this.ordersByProperyId = data;
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *downloadValuationReport(
    loanId: number,
    propertyId: number,
    clientValuationReferenceId: number
  ) {
    try {
      const response = yield this.appraisalDetailsV2Service.downloadValuationReport(
        loanId,
        propertyId,
        clientValuationReferenceId
      );
      downloadDocument(response?.data, response?.headers, 'download');
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while downloading the valuation report file.',
      });
    }
  }

  *createExtraPaymentLink() {
    try {
      const values = this.appraisalActionsFormStore.getFormValues();
      const isValid = this.validateForm({
        propertyRequired: true,
        orderRequired: true,
        amountRequired: true,
      });

      if (!isValid) {
        this.globalStore.notificationStore.showWarningNotification({
          message: GENERIC_VALIDATION_NOTIFICATION,
        });
      } else {
        const response = yield this.appraisalService.createExtraPaymentLink({
          loanId: this.loanId,
          clientValuationReferenceIds: values.clientValuationReferenceIds,
          fee: values.amount,
        });
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Extra payment link created successfully',
        });
        return response?.data?.data ?? null;
      }
      return null;
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
      return null;
    }
  }

  *valuationReorder() {
    try {
      const values = this.appraisalActionsFormStore.getFormValues();
      const isValid = this.validateForm({
        propertyRequired: true,
        orderRequired: true,
        amountRequired: true,
      });

      if (!isValid) {
        this.globalStore.notificationStore.showWarningNotification({
          message: GENERIC_VALIDATION_NOTIFICATION,
        });
      } else {
        // Call API to create extra payment link with the form values
        console.log('Valuation Reorder:', {
          propertyIds: values.propertyIds,
          clientValuationReferenceIds: values.clientValuationReferenceIds,
          reason: values.reason,
          amount: values.amount,
        });
      }
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *valuationRecertification() {
    try {
      const values = this.appraisalActionsFormStore.getFormValues();
      const isValid = this.validateForm({
        propertyRequired: true,
        orderRequired: true,
      });

      if (!isValid) {
        this.globalStore.notificationStore.showWarningNotification({
          message: GENERIC_VALIDATION_NOTIFICATION,
        });
      } else {
        // Call API to create extra payment link with the form values
        console.log('Valuation Recertification:', {
          propertyIds: values.propertyIds,
          clientValuationReferenceIds: values.clientValuationReferenceIds,
          reason: values.reason,
          amount: values.amount,
        });
      }
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *submitReconsiderationOfValue(clientValuationReferenceId: number) {
    try {
      const values = this.appraisalActionsFormStore.getFormValues();
      const isValid = this.validateForm({
        reasonRequired: true,
        uploadRequired: true
      });

      if (!isValid) {
        this.globalStore.notificationStore.showWarningNotification({
          message: GENERIC_VALIDATION_NOTIFICATION,
        });
      } else {
        this.appraisalDetailsV2Service.submitReconsiderationOfValue(
          clientValuationReferenceId,
          values.reason,
          this.uploadedFile
        );
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Dispute submitted successfully',
        });
      }
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *startDispute(clientValuationReferenceId: number, reason: string, disputeType: string, isInternalUser: boolean) {
    try {
      if (!reason?.trim()) {
        this.globalStore.notificationStore.showWarningNotification({
          message: 'Please provide a reason for the dispute.',
        });
        return null;
      }
      const response = yield this.appraisalDetailsV2Service.startDispute(clientValuationReferenceId, reason, disputeType, isInternalUser);
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Dispute initiated successfully.',
      });
      const data = response?.data?.data;
      return { taskId: data?.taskId as number, taskTitle: data?.taskTitle as string };
    } catch (e) {
      const serverMessage = (e as any)?.error?.response?.data?.error?.message;
      this.globalStore.notificationStore.showErrorNotification({
        message: serverMessage || GENERIC_ERROR_MESSAGE,
      });
      return null;
    }
  }

  *setAsSelectedPrimary(loanPropertyValuationId: number, onSuccess?: () => void) {
    try {
      const response = yield this.appraisalDetailsV2Service.setAsSelectedPrimary(loanPropertyValuationId);
      const apiResponse = response?.data;
      if (apiResponse?.status === 'OK') {
        this.globalStore.notificationStore.showSuccessNotification({
          message: apiResponse?.data ?? 'Primary appraisal updated successfully.',
        });
        onSuccess?.();
        return true;
      } else {
        const message = apiResponse?.error?.message ?? GENERIC_ERROR_MESSAGE;
        this.globalStore.notificationStore.showErrorNotification({ message });
        return false;
      }
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
      return false;
    }
  }

  *submitRevision(clientValuationReferenceId: number) {
    try {
      const values = this.appraisalActionsFormStore.getFormValues();
      const isValid = this.validateForm({
        reasonRequired: true,
        uploadRequired: true
      });

      if (!isValid) {
        this.globalStore.notificationStore.showWarningNotification({
          message: GENERIC_VALIDATION_NOTIFICATION,
        });
      } else {
        this.appraisalDetailsV2Service.submitRevison(
          clientValuationReferenceId,
          values.reason,
          this.uploadedFile
        );
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Revision submitted successfully',
        });
      }
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *valuationTransfer() {
    try {
      const values = this.appraisalActionsFormStore.getFormValues();
      const isValid = this.validateForm({
        propertyRequired: true,
        orderRequired: true,
      });

      if (!isValid) {
        this.globalStore.notificationStore.showWarningNotification({
          message: GENERIC_VALIDATION_NOTIFICATION,
        });
      } else {
        // Call API to create extra payment link with the form values
        console.log('Valuation Transfer:', {
          propertyIds: values.propertyIds,
          clientValuationReferenceIds: values.clientValuationReferenceIds,
          reason: values.reason,
          amount: values.amount,
        });
      }
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  resetLinkOrder() {
    this.linkOrderLookupResult = null;
    this.isLinkOrderLoading = false;
  }

  *lookupOrder(orderNumber: string, loanId: number) {
    this.isLinkOrderLoading = true;
    try {
      const response = yield this.appraisalDetailsV2Service.lookupOrder(orderNumber, loanId);
      this.linkOrderLookupResult = response.data.data ?? null;
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
      this.linkOrderLookupResult = null;
    } finally {
      this.isLinkOrderLoading = false;
    }
  }

  *submitLinkOrder(payload: {
    loanId: number;
    propertyId: number;
    orderNumber: string;
    isExistingOrder: boolean;
    appraisalType?: string;
    loanPropertyValuationId?: number | null;
    propertyValuationId?: number | null;
    isPushNewOrder?: boolean;
  }) {
    try {
      yield this.appraisalDetailsV2Service.linkOrder(payload);
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Order linked successfully',
      });
      return true;
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
      return false;
    }
  }

  *createLpvAndPaymentLink(loanId: number, propertyId: number, subType: string) {
    try {
      yield this.appraisalDetailsV2Service.createLpvAndPaymentLink({ loanId, propertyId, subType });
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Payment link request submitted successfully',
      });
      return true;
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
      return false;
    }
  }

  *pushNewOrder(loanId: number, propertyId: number, appraisalType: string) {
    try {
      yield this.appraisalDetailsV2Service.pushNewOrder({ loanId, propertyId, appraisalType });
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Order pushed successfully',
      });
      return true;
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
      return false;
    }
  }
}
